import type { FC } from 'react';

import Pill from '@/common-components/Pill';
import { formatRupees } from '@/utils/currency.util';
import type { AdminDamageReportItem } from '@/types/rental.types';

import { DAMAGE_TYPE_LABEL } from '../constants';

interface DisputesListProps {
  disputes: AdminDamageReportItem[];
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const statusPill = (status: AdminDamageReportItem['status']): { label: string; tone: 'success' | 'warn' | 'danger' } => {
  if (status === 'resolved') return { label: '✓ Resolved', tone: 'success' };
  if (status === 'under_review') return { label: 'Under review', tone: 'warn' };
  return { label: 'Open', tone: 'danger' };
};

const DisputesList: FC<DisputesListProps> = ({ disputes }) => (
  <div className="flex flex-col gap-3 p-4 md:p-5">
    {disputes.map((dispute) => {
      const status = statusPill(dispute.status);
      const approved = dispute.resolution.approvedDeductionPaise;
      return (
        <div
          key={dispute.id}
          className="rounded-xl border border-[var(--color-divider)] bg-white p-4 shadow-[var(--shadow-card)]"
          style={dispute.status !== 'resolved' ? { borderLeft: '3px solid var(--color-danger)' } : undefined}
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13.5px] font-bold text-[var(--color-ink)]">
                  {dispute.listingTitle}
                </span>
                <span className="font-mono text-[11px] text-[var(--color-ink-soft)]">
                  #{dispute.bookingCode}
                </span>
                <Pill tone="warn">{DAMAGE_TYPE_LABEL[dispute.type]}</Pill>
              </div>
              <div className="mt-1 text-[12px] text-[var(--color-ink-muted)]">
                {dispute.ownerName} reported {dispute.borrowerName} · {formatDate(dispute.createdAt)}
              </div>
            </div>
            <Pill tone={status.tone}>{status.label}</Pill>
          </div>

          <p className="mt-2.5 text-[12.5px] leading-relaxed text-[var(--color-ink-muted)]">
            “{dispute.description}”
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px]">
            <span className="text-[var(--color-ink-muted)]">
              Deposit:{' '}
              <span className="font-semibold text-[var(--color-ink)]">
                {formatRupees(dispute.depositPaise)}
              </span>
            </span>
            <span className="text-[var(--color-ink-muted)]">
              Requested:{' '}
              <span className="font-semibold text-[var(--color-danger-ink)]">
                {formatRupees(dispute.requestedDeductionPaise)}
              </span>
            </span>
            {approved !== undefined ? (
              <span className="text-[var(--color-ink-muted)]">
                Deducted:{' '}
                <span className="font-semibold text-[var(--color-success-ink)]">
                  {formatRupees(approved)}
                </span>
              </span>
            ) : null}
            {dispute.photos.length > 0 ? (
              <span className="text-[var(--color-ink-muted)]">
                📷 {dispute.photos.length} photo{dispute.photos.length === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
        </div>
      );
    })}
  </div>
);

export default DisputesList;
